<template>
  <div id="formula_builder" class="flex flex-col gap-2">
    <FormulaInput
      ref="formulaInputRef"
      v-model="formulaValue"
      placeholder="Enter formula"
      :number-attributes="numberAttributes"
    />

    <div class="flex flex-row items-center gap-6 primary_color">
      <SearchablePopoverSelector
        empty-message="No properties found"
        :options="propertyOptions"
        overlay-class-name="formula_selector_popover"
        @select="handlePropertySelect"
      >
        <div class="flex flex-row items-center gap-1 cursor-pointer">
          <span class="text-sm"> Properties </span>

          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW_GREY"
            class="size-4"
            alt="arrow_icon"
          />
        </div>
      </SearchablePopoverSelector>

      <SearchablePopoverSelector
        empty-message="No functions found"
        :grouped="hasMultipleGroups"
        :groups="operatorGroups"
        :options="flatOperatorOptions"
        overlay-class-name="formula_selector_popover"
        @select="handleOperatorSelect"
      >
        <div class="flex flex-row items-center gap-1 cursor-pointer">
          <span class="text-sm"> Functions </span>

          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW_GREY"
            class="size-4"
            alt="arrow_icon"
          />
        </div>
      </SearchablePopoverSelector>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  operatorCategories,
  componentOperatorConfig,
  operatorsByCategory,
  getOperatorsForComponent,
  formulaConfigKeys,
} from "@/src/constants/formulaConstants.js";
import FormulaInput from "@/src/components/formula_components/FormulaInput.vue";
import SearchablePopoverSelector from "@/src/components/SearchablePopoverSelector.vue";

const props = defineProps({
  modelValue: { type: String, default: "" },
  numberAttributes: { type: Array, default: () => [] },
  configKey: { type: String, default: formulaConfigKeys.CALCULATED_FIELDS },
});

const emit = defineEmits(["update:modelValue"]);

const formulaInputRef = ref(null);

const formulaValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const operatorGroups = computed(() => {
  return getOperatorsForComponent(props.configKey);
});

const hasMultipleGroups = computed(() => {
  return operatorGroups.value.length > 1;
});

const flatOperatorOptions = computed(() => {
  if (hasMultipleGroups.value) return [];
  return operatorGroups.value[0]?.operators || [];
});

const propertyOptions = computed(() =>
  props.numberAttributes.map((attr) => ({
    value: attr.id,
    label: attr.name,
  })),
);

const getPropertyName = (id) => {
  try {
    const attr = props.numberAttributes.find((a) => a.id === id);
    return attr?.name || id;
  } catch (error) {
    console.error("An error occurred while getting property name: ", error);
    return id;
  }
};

const handleOperatorSelect = (value) => {
  try {
    if (!value || !formulaInputRef.value) return;
    formulaInputRef.value.insertOperator(value);
  } catch (error) {
    console.error("An error occurred while handling operator select: ", error);
  }
};

const handlePropertySelect = (value) => {
  try {
    if (!value || !formulaInputRef.value) return;
    const name = getPropertyName(value);
    formulaInputRef.value.insertProperty(value, name);
  } catch (error) {
    console.error("An error occurred while handling property select: ", error);
  }
};
</script>

<style>
.formula_selector_popover {
  z-index: 1050;
}

.formula_selector_popover .ant-popover-inner {
  background: var(--dropdown-bg);
  border-radius: 8px;
  padding: 0;
}

.formula_selector_popover .ant-popover-arrow::before {
  background: var(--dropdown-bg);
}

.formula_selector_popover .ant-popover-inner-content {
  padding: 0;
  max-height: calc(100vh - 200px);
  overflow: hidden;
}
</style>
